import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { logError } from '../utils/log';

const gameStructure = [
    { name: 'Game 1: 301 Singles', type: 'Singles' },
    { name: 'Game 2: 301 Singles', type: 'Singles' },
    { name: 'Game 3: 301 Doubles', type: 'Doubles' },
    { name: 'Game 4: 501 Singles', type: 'Singles' },
    { name: 'Game 5: 501 Singles', type: 'Singles' },
    { name: 'Game 6: 501 Doubles', type: 'Doubles' },
    { name: 'Game 7: Cricket Singles', type: 'Singles' },
    { name: 'Game 8: Cricket Singles', type: 'Singles' },
    { name: 'Game 9: Cricket Doubles', type: 'Doubles' },
];

export const MatchResultsSummaryPage = ({ matchData, navigate }) => {
    const [schedule, setSchedule] = useState(null);
    const [teams, setTeams] = useState([]);
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!matchData) {
            setError("No match data provided.");
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                // Fetch all players and teams once for easy lookup
                const playersSnapshot = await getDocs(collection(db, "users"));
                setPlayers(playersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

                const teamsSnapshot = await getDocs(collection(db, "teams"));
                setTeams(teamsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

                // Fetch the specific schedule document
                const scheduleDocRef = doc(db, "schedules", matchData.scheduleId);
                const scheduleDoc = await getDoc(scheduleDocRef);

                if (scheduleDoc.exists()) {
                    setSchedule(scheduleDoc.data());
                } else {
                    setError("Schedule not found.");
                }
            } catch (err) {
                setError("Failed to fetch match details.");
                logError(err);
            }
            setLoading(false);
        };

        fetchData();
    }, [matchData]);

    const getPlayerName = (playerId) => {
        const player = players.find(p => p.id === playerId);
        return player ? (player.nickname || player.firstName) : 'N/A';
    };

    if (loading) return <div className="text-center">Loading Match Results...</div>;
    if (error) return <p className="text-center text-danger">{error}</p>;
    if (!schedule) return <p className="text-center">Could not load schedule details.</p>;

    const week = schedule.weeks[matchData.weekIndex];
    const game = week.games[matchData.gameIndex];
    const results = game.results;

    if (!results) return <p className="text-center">No results have been entered for this match yet.</p>;

    const homeTeam = teams.find(t => t.id === game.homeTeamId);
    const awayTeam = teams.find(t => t.id === game.awayTeamId);

    const fromPage = matchData?.from || 'Schedule'; // Default to 'Schedule' if not provided
    const handleBackNavigation = () => {
        const backProps = {};
        if (fromPage === 'Schedule') {
            backProps.scheduleId = matchData.scheduleId;
            backProps.weekIndex = matchData.weekIndex;
        }
        navigate(fromPage, backProps);
    };

    return (
        <div className="space-y-6">
            {/* Back Navigation Button */}
            <div className="flex">
                <button onClick={handleBackNavigation} className="btn btn-secondary inline-flex items-center">
                    &larr; Back to {fromPage}
                </button>
            </div>
            <h2 className="text-4xl font-extrabold text-center heading-primary-1">Match Summary</h2>
            <div className="text-center">
                <p className="text-2xl font-bold text-strong">{awayTeam?.name} at {homeTeam?.name}</p>
                <p className="text-lg text-muted">Week {week.week} - {week.date}</p>
                <p className="text-4xl font-black subheading-primary-1 mt-2">
                    {(() => {
                        const parts = (game.result || '').split('-');
                        // Align with SchedulePage: display as Home - Away.
                        // Data's game.result appears stored as Away-Home; swap order here.
                        const awayScore = parseInt(parts[0]?.trim(), 10);
                        const homeScore = parseInt(parts[1]?.trim(), 10);
                        if (!isFinite(homeScore) || !isFinite(awayScore)) return game.result || '';
                        return `${homeScore} - ${awayScore}`;
                    })()}
                </p>
            </div>

            {/* Game-by-Game Summary */}
            <div className="card p-6 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold mb-4">Game-by-Game</h3>
                {/* Table view for md+ screens */}
                <div className="overflow-x-auto hidden md:block">
                    <table className="min-w-max sm:min-w-full text-sm">
                    <thead>
                            <tr className="table-header">
                            <th className="py-2 text-left font-semibold">Game</th>
                            <th className="py-2 text-left font-semibold">{awayTeam?.name}</th>
                            <th className="py-2 text-center font-semibold">Score</th>
                            <th className="py-2 text-right font-semibold">{homeTeam?.name}</th>
                        </tr>
                    </thead>
                    <tbody>
                            {results.gameScores.map((score, idx) => {
                            const homeP1 = getPlayerName(results.playerAssignments[`${idx}-home-p1`]);
                            const homeP2 = getPlayerName(results.playerAssignments[`${idx}-home-p2`]);
                            const awayP1 = getPlayerName(results.playerAssignments[`${idx}-away-p1`]);
                            const awayP2 = getPlayerName(results.playerAssignments[`${idx}-away-p2`]);
                            const homePlayers = gameStructure[idx].type === 'Singles' ? homeP1 : `${homeP1} & ${homeP2}`;
                            const awayPlayers = gameStructure[idx].type === 'Singles' ? awayP1 : `${awayP1} & ${awayP2}`;
                                const homeWins = score.home > score.away;
                                const awayWins = score.away > score.home;
                            return (
                                    <tr key={idx} className={`${idx % 2 === 1 ? 'row-alt' : ''} border-b border-low`}>
                                        <td className="py-3 px-2 font-medium">{gameStructure[idx].name}</td>
                                        <td className={`py-3 px-2 ${awayWins ? 'font-bold' : ''}`}>{awayPlayers}</td>
                                        <td className="py-3 px-2 text-center font-bold">
                                            <span className={awayWins ? 'text-success' : ''}>{score.away}</span> - <span className={homeWins ? 'text-success' : ''}>{score.home}</span>
                                        </td>
                                        <td className={`py-3 px-2 text-right ${homeWins ? 'font-bold' : ''}`}>{homePlayers}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                </div>
                {/* Mobile card view (Away @ Home, ordered) */}
                <div className="md:hidden space-y-4">
                    {([0,3,6]).map((startIdx) => {
                        const groupLabel = (gameStructure[startIdx]?.name || '').match(/301|501|Cricket/)?.[0] || '';
                        const idxs = [startIdx, startIdx + 1, startIdx + 2];
                        const tone = groupLabel === '301' ? 'bg-primary-soft' : groupLabel === '501' ? 'bg-danger-soft' : 'bg-success-soft';
                        return (
                            <div key={`group-${startIdx}`} className={`rounded-md p-3 shadow-sm ${tone}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-muted">{groupLabel} Series</p>
                                    <span className="text-[11px] text-muted">2 Singles · 1 Doubles</span>
                                </div>
                                <div className="space-y-2">
                                    {idxs.map((idx) => {
                                        const score = results.gameScores[idx];
                                        const type = gameStructure[idx]?.type;
                                        if (!score || !type) return null;
                                        const homeP1 = getPlayerName(results.playerAssignments[`${idx}-home-p1`]);
                                        const homeP2 = getPlayerName(results.playerAssignments[`${idx}-home-p2`]);
                                        const awayP1 = getPlayerName(results.playerAssignments[`${idx}-away-p1`]);
                                        const awayP2 = getPlayerName(results.playerAssignments[`${idx}-away-p2`]);
                                        const homePlayers = type === 'Singles' ? homeP1 : `${homeP1} & ${homeP2}`;
                                        const awayPlayers = type === 'Singles' ? awayP1 : `${awayP1} & ${awayP2}`;
                                        const homeWins = score.home > score.away;
                                        const awayWins = score.away > score.home;
                                        return (
                                            <div key={`g-${idx}`} className="flex items-center justify-between py-1.5">
                                                <div className="flex-1 pr-2">
                                                    <div className={`text-sm ${awayWins ? 'font-semibold' : 'font-normal'}`}>{awayPlayers}</div>
                                                </div>
                                                <div className="min-w-[80px] text-center">
                                                    <span className="text-base font-semibold tracking-wide">
                                                        <span className={awayWins ? 'text-success' : ''}>{score.away}</span>
                                                        <span className="mx-1">-</span>
                                                        <span className={homeWins ? 'text-success' : ''}>{score.home}</span>
                                                    </span>
                                                </div>
                                                <div className="flex-1 pl-2 text-right">
                                                    <div className={`text-sm ${homeWins ? 'font-semibold' : 'font-normal'}`}>{homePlayers}</div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Highlights Summary */}
            <div className="card p-6 rounded-lg shadow-lg">
                <h3 className="text-2xl font-bold mb-4">Match Highlights</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                        <h4 className="text-xl font-semibold mb-2">{homeTeam?.name}</h4>
                        {homeTeam?.members.map(playerId => 
                            results.highlights && results.highlights[playerId] && (
                                <HighlightList key={playerId} highlights={results.highlights[playerId]} playerName={getPlayerName(playerId)} />
                            )
                        )}
                    </div>
                    <div>
                        <h4 className="text-xl font-semibold mb-2">{awayTeam?.name}</h4>
                        {awayTeam?.members.map(playerId => 
                            results.highlights && results.highlights[playerId] && (
                                <HighlightList key={playerId} highlights={results.highlights[playerId]} playerName={getPlayerName(playerId)} />
                            )
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const HighlightList = ({ playerName, highlights }) => (
    <div className="mb-4 surface-low border border-low p-4 rounded-lg">
        <p className="font-bold text-lg heading-primary-1">{playerName}</p>
        <div className="mt-2 text-sm">
            {highlights.tons && (
                <div className="flex items-center justify-between py-1 border-b border-low last:border-b-0">
                    <span className="break-words">Tons:</span>
                    <span className="font-semibold">{highlights.tons}</span>
                </div>
            )}
            {highlights.on80 && (
                <div className="flex items-center justify-between py-1 border-b border-low last:border-b-0">
                    <span className="break-words">80+ On:</span>
                    <span className="font-semibold">{highlights.on80}</span>
                </div>
            )}
            {highlights.highOut && (
                <div className="flex items-center justify-between py-1 border-b border-low last:border-b-0">
                    <span className="break-words">80+ Out:</span>
                    <span className="font-semibold">{highlights.highOut}</span>
                </div>
            )}
            {highlights['180s'] > 0 && (
                <div className="flex items-center justify-between py-1 border-b border-low last:border-b-0">
                    <span className="break-words">180s:</span>
                    <span className="font-semibold">{highlights['180s']}</span>
                </div>
            )}
            {highlights.c6 > 0 && (
                <div className="flex items-center justify-between py-1 border-b border-low last:border-b-0">
                    <span className="break-words">6-Hits:</span>
                    <span className="font-semibold">{highlights.c6}</span>
                </div>
            )}
            {highlights.c7 > 0 && (
                <div className="flex items-center justify-between py-1 border-b border-low last:border-b-0">
                    <span className="break-words">7-Hits:</span>
                    <span className="font-semibold">{highlights.c7}</span>
                </div>
            )}
            {highlights.c8 > 0 && (
                <div className="flex items-center justify-between py-1 border-b border-low last:border-b-0">
                    <span className="break-words">8-Hits:</span>
                    <span className="font-semibold">{highlights.c8}</span>
                </div>
            )}
            {highlights.c9 > 0 && (
                <div className="flex items-center justify-between py-1 border-b border-low last:border-b-0">
                    <span className="break-words">9-Hits:</span>
                    <span className="font-semibold">{highlights.c9}</span>
                </div>
            )}
            {highlights.deadeye > 0 && (
                <div className="flex items-center justify-between py-1">
                    <span className="break-words">Deadeyes:</span>
                    <span className="font-semibold">{highlights.deadeye}</span>
                </div>
            )}
        </div>
    </div>
);